import React, { useEffect, useRef, useState } from "react";
import { LifeBuoy, Phone } from "lucide-react";

const IN = { CH: "in der Schweiz" };
const tel = (n) => `tel:${n.replace(/\s/g, "")}`;

const Stelle = ({ h, land }) => (
  <li className="flex flex-col gap-0.5 rounded-md bg-white/70 border border-amber-200 px-3 py-2" data-testid={`safety-stelle-${land}-${h.nummer.replace(/\s/g, "")}`}>
    <span className="text-sm font-semibold">{h.name}</span>
    <a href={tel(h.nummer)} className="inline-flex items-center gap-1.5 text-lg font-bold text-amber-900 underline-offset-2 hover:underline" data-testid="safety-notice-kontakt">
      <Phone className="h-4 w-4" aria-hidden="true" />{h.nummer}
    </a>
    {h.alternativ?.length > 0 && (
      <span className="text-xs text-slate-600">oder {h.alternativ.map((a, i) => (
        <React.Fragment key={a}>{i > 0 && " · "}<a href={tel(a)} className="underline">{a}</a></React.Fragment>))}</span>
    )}
    <span className="text-xs text-slate-600">{h.erreichbarkeit}</span>
  </li>
);

const Gruppe = ({ titel, liste, land }) => liste.length > 0 && (
  <div className="mt-3">
    <p className="text-xs font-bold uppercase tracking-widest text-amber-800 mb-1.5">{titel}</p>
    <ul className="space-y-2">{liste.map((h) => <Stelle key={h.nummer} h={h} land={land} />)}</ul>
  </div>
);

export const SafetyNotice = ({ data }) => {
  const [land, setLand] = useState(null);
  const listeRef = useRef(null);
  useEffect(() => setLand(null), [data]);
  useEffect(() => { if (land) listeRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [land]);
  const laender = Object.entries(data.laender || {});
  const liste = land ? data.hilfestellen[land] : [];
  return (
    <div role="alert" data-testid="safety-notice" className="rounded-md border-2 border-amber-300 bg-amber-50 p-5 text-slate-900">
      <div className="flex items-center gap-2 mb-2">
        <LifeBuoy className="h-5 w-5 text-amber-700" aria-hidden="true" />
        <p className="text-xs font-bold uppercase tracking-widest text-amber-800">Wichtig</p>
      </div>
      <p className="text-base leading-relaxed" data-testid="safety-notice-text">{data.text}</p>
      {laender.length > 0 && (
        <div className="mt-4" data-testid="safety-notice-stellen">
          <p className="text-sm font-medium">Wo bist du gerade? Tipp auf dein Land, dann siehst du, wen du anrufen kannst.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {laender.map(([code, name], i) => (
              <button key={code} type="button" onClick={() => setLand(code)} aria-pressed={land === code} data-testid={`safety-land-${code}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold border-2 transition-colors ${land === code ? "bg-amber-700 border-amber-700 text-white" : i === 0 ? "bg-amber-100 border-amber-600 text-amber-900 hover:bg-amber-200" : "bg-white border-amber-300 text-amber-900 hover:bg-amber-100"}`}>
                Hilfe {IN[code] || `in ${name}`}
              </button>
            ))}
          </div>
          {land && (
            <div ref={listeRef} data-testid={`safety-liste-${land}`}>
              <Gruppe titel="Bei unmittelbarer Gefahr" liste={liste.filter((h) => h.akut)} land={land} />
              <Gruppe titel="Reden und Beratung" liste={liste.filter((h) => !h.akut)} land={land} />
              <p className="mt-3 text-xs text-slate-600">Ist eine Leitung besetzt, versuch es gleich noch einmal.
                {data.kategorie === "gewalt" && " Wenn jemand dein Telefon kontrolliert, ruf möglichst von einem sicheren Gerät an."}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SafetyNotice;
